/**
 * Which finished-job messages this tab is already showing on its own.
 *
 * The server tells every open tab when a caption render, AI clip ideas or a
 * deck recording finishes (`job:done`, components/JobDoneNotifier.js). A page
 * that is polling that same job already updates its card and says "ready",
 * so the global toast would say it twice. Pages register what they poll;
 * the notifier skips those.
 *
 * Keys are `<kind>:<itemId>`, matching the server's payload: `captions:<clip
 * uid>`, `ai-clips:<webinar id>`, `deck-render:<deck id>`. Counted, so two
 * pollers on one job don't unwatch each other.
 */

const watched = new Map();

// A poller stops the moment it sees the result, and the server's message can
// land a moment later. Keep the key a little longer so that page's own
// "ready" isn't followed by a second one from the notifier.
export const UNWATCH_GRACE_MS = 15000;

export function jobKey(kind, itemId) {
  return `${kind}:${itemId}`;
}

/** Start watching; returns the function that stops (after UNWATCH_GRACE_MS). Safe to call twice. */
export function watchJob(kind, itemId) {
  if (!kind || !itemId) return () => {};
  const key = jobKey(kind, itemId);
  watched.set(key, (watched.get(key) || 0) + 1);
  let done = false;
  return () => {
    if (done) return;
    done = true;
    setTimeout(() => {
      const n = (watched.get(key) || 1) - 1;
      if (n <= 0) watched.delete(key);
      else watched.set(key, n);
    }, UNWATCH_GRACE_MS);
  };
}

export function isJobWatched(kind, itemId) {
  return watched.has(jobKey(kind, itemId));
}

export function _resetJobWatch() {
  watched.clear();
}
